<template>
  <div class="w-full h-full pt-5">
    <div class="flex">
      <back-button
        label="Back"
        @onClick="$router.back()"
        variant="secondary"
        class="mr-4"
      />
      <div
        class="mt-4"
        style="border-left: 1px solid #878e99; height: 1.5rem"
      />
      <h1 class="text-xl text-left font-extrabold ml-4 mt-3 w-auto">
        Employee Agreement
      </h1>
      <div class="w-7/12 mt-4 ml-8">
        <Breadcrumb :items="breadcrumbs" />
      </div>
    </div>
    <div style="height: 100%" class="mt-20" v-if="loadingData">
      <loader size="xxs" :loader-image="false" />
    </div>
    <div class="px-3" v-if="!loadingData">
      <card class="p-5 mt-4 flex flex-col">
        <div class="w-full flex mb-4">
          <span class="text-sm font-semibold text-romanSilver uppercase">
            Employee :
          </span>
          <span class="text-sm font-bold ml-1">
            {{ `${employee.fname} ${employee.lname}` }}
          </span>
        </div>
        <div class="w-full flex">
          <img
            v-if="employee.photo !== '' && employee.photo !== null"
            class="mr-3 rounded"
            style="width: 135px; height: 128px"
            :src="employee.photo"
            alt="profile"
          />
          <div
            style="height: 138px; width: 138px; border-radius: 5px"
            class="text-blueCrayola border text-center font-black text-lg pt-14 mr-2"
            v-else
          >
            {{ $getInitials(`${employee.fname} ${employee.lname}`) }}
          </div>
          <div class="w-full flex flex-col">
            <div class="w-full flex">
              <card class="flex p-3 mb-4 w-auto mr-3 flex-grow">
                <div class="flex flex-col">
                  <div class="flex">
                    <icon icon-name="icon-suitcase" size="xs" />
                    <span
                      class="ml-1 text-carrotOrange font-bold uppercase text-xs"
                    >
                      Designation
                    </span>
                  </div>
                  <div class="text-xs ml-5 mt-1 font-semibold uppercase">
                    {{ employee.designation }}
                  </div>
                </div>
              </card>
              <card class="flex p-3 mb-4 mr-3 w-auto flex-grow">
                <div class="flex flex-col">
                  <div class="flex">
                    <icon icon-name="icon-department" size="xs" />
                    <span
                      class="ml-1 text-blueCrayola uppercase font-bold text-xs"
                    >
                      Function
                    </span>
                  </div>
                  <div class="text-xs ml-5 mt-1 font-semibold uppercase">
                    {{ employee.function }}
                  </div>
                </div>
              </card>
              <card class="flex p-3 w-auto mb-4 mr-3 flex-grow">
                <div class="flex flex-col">
                  <div class="flex">
                    <icon icon-name="line-manager" color="#FFFFFF" size="xs" />
                    <span
                      class="ml-1 text-mediumSeaGreen font-bold uppercase text-xs"
                    >
                      Line Manager
                    </span>
                  </div>
                  <div class="text-xs ml-5 mt-1 font-semibold uppercase">
                    {{
                      employee.lineManager ? employee.lineManager.fname : "---"
                    }}
                    {{
                      employee.lineManager ? employee.lineManager.lname : "---"
                    }}
                  </div>
                </div>
              </card>
            </div>
            <div class="w-full flex">
              <card class="flex p-3 mr-3 w-auto mb-4 flex-grow">
                <div class="flex flex-col">
                  <div class="flex">
                    <icon
                      icon-name="icon-user-list"
                      color="#FFFFFF"
                      size="xs"
                    />
                    <span
                      class="ml-1 text-blueCrayola font-bold uppercase text-xs"
                    >
                      Employee ID
                    </span>
                  </div>
                  <div class="text-xs ml-5 mt-1 font-semibold uppercase">
                    {{ employee.employeeId }}
                  </div>
                </div>
              </card>
              <card class="flex p-3 mb-4 mr-3 flex-grow">
                <div class="flex flex-col">
                  <div class="flex">
                    <icon icon-name="map-pin" size="xms" />
                    <span class="ml-2 text-flame font-bold uppercase text-xs">
                      Office Location
                    </span>
                  </div>
                  <div class="text-xs ml-5 mt-1 font-semibold uppercase">
                    {{ employee.location }}
                  </div>
                </div>
              </card>
            </div>
          </div>
        </div>
      </card>
    </div>

    <ValidationObserver rules="required" v-slot="{ handleSubmit }">
      <div class="px-3" v-if="!loadingData">
        <card class="p-5" style="margin-top: 4px">
          <div
            class="w-full flex flex-row justify-between items-center"
            style="margin-top: 20px"
          >
            <div class="w-full flex h-auto mt-2">
              <h1 class="text-base font-bold">Agreement Details</h1>
            </div>

            <div class="h-full w-full flex">
              <div class="p-2 w-full flex">
                <div class="flex w-full justify-end gap-4 -mt-2">
                  <Badge
                    label="In-Review"
                    variant="primary"
                    background-color="rgba(241, 90, 41, 0.12)"
                    color="rgba(241, 90, 41, 1)"
                    style="
                      padding: 9px;
                      border-radius: 5px;
                      font-size: 14px;
                      font-weight: 600;
                      line-height: 1.5;
                    "
                    v-if="agreementStatus === 'shared' && !approved"
                  />

                  <Badge
                    label="Pending Approval"
                    variant="primary"
                    background-color="rgba(233, 147, 35, 0.12)"
                    color="rgba(233, 147, 35, 1)"
                    style="
                      padding: 9px;
                      border-radius: 5px;
                      font-size: 14px;
                      font-weight: 600;
                      line-height: 1.5;
                    "
                    v-if="agreementStatus === 'submitted' && !approved"
                  />

                  <Badge
                    label="Approved"
                    variant="primary"
                    background-color="rgba(19, 181, 106, 0.08)"
                    color="#13B56A"
                    style="
                      padding: 9px;
                      border-radius: 5px;
                      font-size: 14px;
                      font-weight: 600;
                      line-height: 1.5;
                    "
                    v-if="approved"
                  />

                  <div
                    class="w-auto flex items-center cursor-pointer gap-1"
                    v-if="approved"
                    @click="
                      viewStatusReports($route.params.id, $route.params.year)
                    "
                  >
                    <Icon icon-name="overview-icon" class="light" size="xsm" />
                    <p class="text-xs uppercase text-flame font-bold">
                      Status Report Overview
                    </p>
                  </div>

                  <!----<Button class="text-center font-semibold flex p-1 text-base ml-5 -mr-10" width="12rem" height="2rem"
                    @click="commentModal = true">
                    <div class="flex">
                      <span class="text-flame">
                        <Icon icon-name="icon-message-outline" class-name="mr-2 w-5 h-5" size="xsm" />
                      </span>
                      <span class="mr-1">
                        {{ unreadComments }}
                      </span>
                      Comments
                    </div>
                  </Button>--->
                </div>
              </div>
            </div>
          </div>

          <div style="border-bottom: 1px solid #878e99" />

          <div class="">
            <div class="mt-4">
              <div
                class="w-full flex flex-row justify-between"
                style="margin-top: 20px"
              >
                <div class="flex h-auto mt-2">
                  <h1 class="text-base font-bold">KPAs</h1>
                </div>

                <div class="w-auto flex items-center">
                  <div
                    class="w-auto flex cursor-pointer items-center"
                    @click="getEachAgreementComments()"
                  >
                    <Icon
                      icon-name="comment-plus-icon"
                      style="color: white"
                      size="xsm"
                    />
                  </div>

                  <div class="h-4 mx-3 w-px bg-gray-400" />

                  <div
                    class="flex items-center justify-between w-full h-full p-2 bg-ghostWhite rounded"
                  >
                    <span class="text-sm font-black whitespace-nowrap">
                      Total Allocated Weight:
                    </span>
                    <div class="flex items-center ml-1 gap-2">
                      <span class="text-sm text-desire">
                        {{ allocatedWeight }}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="border-b-2" style="height: 1rem" />

              <div class="flex flex-col w-full my-4">
                <!----- List of KPA's ---->

                <div class="w-full h-auto flex flex-col">
                  <accordion
                    class="mt-6"
                    v-for="(kpaTypes, kpaNumber) in templateKPATypes"
                    :key="'kpa-' + kpaNumber"
                    show-divider
                    :is-open="true"
                  >
                    <template v-slot:title>
                      <ValidationObserver
                        :ref="`kpa-observer-${kpaTypes.id}`"
                        v-slot="{ handleSubmit }"
                      >
                        <div class="flex gap-3">
                          <span class="font-bold">{{ kpaTypes.name }}</span>
                          <span
                            class="flex cursor-pointer"
                            v-if="!approved"
                            @click="handleSubmit(() => createNewKPA(kpaTypes))"
                          >
                            <icon icon-name="icon-plus" size="xs" />
                          </span>
                          <span
                            v-if="!canSubmit && !approved"
                            class="flex cursor-pointer text-blueCrayola"
                            @click="toggleEditMode(kpaTypes)"
                          >
                            <icon
                              :icon-name="
                                isKpaEditable(kpaTypes)
                                  ? 'icon-save'
                                  : 'icon-edit'
                              "
                              size="xs"
                            />
                          </span>
                        </div>
                      </ValidationObserver>
                    </template>

                    <template v-slot:block>
                      <div class="w-auto flex items-center">
                        <div class="w-auto items-center">
                          <Icon
                            icon-name="comment-plus-icon"
                            style="color: white"
                            size="xsm"
                          />
                        </div>

                        <div class="h-4 mx-2 w-px bg-gray-400" />

                        <div class="w-auto flex items-center">
                          <img
                            src="@/assets/images/hugeicons_assignments.svg"
                            style="width: 24px; height: 24px"
                          />
                        </div>

                        <div class="h-4 mx-2 w-px bg-gray-400" />

                        <div
                          class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2"
                        >
                          <span class="font-black text-xs">
                            {{ getKPAWeight(kpaTypes, kpaTypes.id) }} %
                          </span>
                        </div>
                      </div>
                    </template>

                    <div class="flex flex-col w-full">
                      <!-- Strategic Goal Section -->
                      <div
                        class="w-full mb-6 p-4 bg-alertRed rounded-lg shadow-sm"
                      >
                        <h2 class="text-base font-bold text-gray-800 mb-2">
                          STRATEGIC GOAL
                        </h2>
                        <p class="text-gray-600">{{ kpaTypes.description }}</p>
                      </div>

                      <div class="w-full flex flex-col gap-4">
                        <accordion
                          class="px-5 py-3 bg-gray-50 shadow-md rounded-lg -mt-1 mb-0"
                          v-for="(item, index) in kpaTypes.kpas"
                          :key="'kpi-' + index"
                          :is-open="true"
                        >
                          <template v-slot:block>
                            <div
                              class="w-full flex flex-col lg:flex-row items-start justify-between gap-4"
                            >
                              <div
                                class="flex items-start w-full lg:w-auto flex-1 gap-2"
                              >
                                <div
                                  class="flex items-center"
                                  :class="
                                    !item.isEdit
                                      ? 'h-7 mt-1 cursor-default'
                                      : 'h-8 mt-2 cursor-pointer'
                                  "
                                >
                                  <span
                                    @click="
                                      !item.isEdit
                                        ? ' '
                                        : deleteKPA(index, kpaTypes)
                                    "
                                    class="text-gray-500 hover:text-red-500 transition-colors"
                                  >
                                    <icon
                                      icon-name="icon-trash"
                                      size="xs"
                                      :class="
                                        !item.isEdit
                                          ? `opacity-50`
                                          : `opacity-100`
                                      "
                                    />
                                  </span>
                                </div>

                                <div
                                  :class="
                                    !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                  "
                                  class="w-auto mx-2 flex items-center"
                                >
                                  <span
                                    class="w-px bg-gray-300"
                                    :class="!item.isEdit ? 'h-6' : 'h-8'"
                                  ></span>
                                </div>

                                <div
                                  v-if="!item.isEdit"
                                  class="flex gap-2 whitespace-nowrap mt-2"
                                >
                                  <span class="font-bold text-flame text-sm"
                                    >KPI:</span
                                  >
                                  <span class="font-semibold text-sm">{{
                                    item.name
                                  }}</span>
                                </div>

                                <div v-else>
                                  <div class="flex items-center gap-2">
                                    <span
                                      class="font-bold text-flame text-sm mt-5 whitespace-nowrap"
                                      >KPI :</span
                                    >
                                    <div class="flex-1">
                                      <c-text
                                        placeholder="--Enter--"
                                        variant="w-full"
                                        class="w-72"
                                        v-model="item.name"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div
                                  :class="
                                    !item.isEdit ? 'h-6 mt-1' : 'h-8 mt-2'
                                  "
                                  class="w-auto mx-2 flex items-center"
                                >
                                  <span
                                    class="w-px bg-gray-300"
                                    :class="!item.isEdit ? 'h-6' : 'h-8'"
                                  ></span>
                                </div>

                                <div
                                  v-if="!item.isEdit"
                                  class="flex gap-2 whitespace-nowrap mt-2"
                                >
                                  <span class="font-bold text-flame text-sm"
                                    >Objective:</span
                                  >
                                  <span class="font-semibold text-sm">{{
                                    item.objective
                                  }}</span>
                                </div>

                                <div v-else>
                                  <div class="flex items-center gap-2">
                                    <span
                                      class="font-bold text-flame text-sm mt-5 whitespace-nowrap"
                                      >Objective :</span
                                    >
                                    <!-- Added whitespace-nowrap -->
                                    <div class="flex-1">
                                      <c-text
                                        placeholder="--Enter--"
                                        variant="w-full"
                                        class="w-72"
                                        v-model="item.objective"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div class="w-auto flex items-center">
                                <div
                                  class="flex cursor-pointer items-center"
                                  @click="getEachAgreementComments(item.kpaId)"
                                >
                                  <Icon
                                    icon-name="comment-plus-icon"
                                    style="color: white"
                                    size="xsm"
                                  />
                                </div>

                                <div
                                  :class="'h-6'"
                                  v-if="approved"
                                  class="w-auto mx-4 flex items-center"
                                >
                                  <span class="w-px bg-gray-300 h-6"></span>
                                </div>

                                <div
                                  class="flex cursor-pointer items-center"
                                  v-if="approved"
                                  @click="viewReport(item, kpaTypes)"
                                >
                                  <img
                                    v-if="
                                      item.report === null ||
                                      item.report === 'null'
                                    "
                                    src="@/assets/images/hugeicons_assignments-gray.svg"
                                    style="width: 24px; height: 24px"
                                  />
                                  <img
                                    v-else
                                    src="@/assets/images/hugeicons_assignments.svg"
                                    style="width: 24px; height: 24px"
                                  />
                                </div>

                                <div
                                  :class="'h-6'"
                                  class="w-auto mx-4 flex items-center"
                                >
                                  <span class="w-px bg-gray-300 h-6"></span>
                                </div>

                                <div
                                  class="p-2 w-auto flex gap-2 items-center rounded-sm mr-2"
                                  v-if="!item.isEdit"
                                >
                                  <span class="font-black text-xs">
                                    {{ item.weight }}%
                                  </span>
                                </div>

                                <div v-else>
                                  <div class="w-auto flex items-center mr-3">
                                    <c-text
                                      placeholder="--Enter Weight--"
                                      variant="w-full cloak"
                                      class="w-14"
                                      type="number"
                                      min="0"
                                      max="100"
                                      v-model="item.weight"
                                      @input="validateWeight(item.weight)"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </template>

                          <div
                            class="w-full h-[1px] mb-6 -mt-4 border border-2"
                          />

                          <div class="w-full flex flex-col gap-1 mb-3">
                            <span class="text-sm font-semibold">Criteria</span>
                          </div>
                          <div
                            class="w-full grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
                          >
                            <div
                              class="label"
                              v-for="(n, rating) in item.kpis"
                              :key="rating"
                            >
                              <c-text
                                variant="w-full"
                                placeholder="Criteria"
                                class="mr-2 -mt-2"
                                :disabled="!item.isEdit"
                                :value="n.criteria"
                              />
                            </div>
                          </div>
                        </accordion>
                      </div>
                    </div>
                  </accordion>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>

      <div class="pl-3 flex mt-5" v-if="!loadingData && !approved">
        <div class="flex flex-grow">
          <Button
            :disabled="disableBtn"
            width="14rem"
            :class="
              disableBtn ? `btn-disabled` : `bg-dynamicBackBtn text-white`
            "
            @click="handleSubmit(checkSelected('Share with Employee', 'all'))"
            v-if="
              (agreementStatus === 'shared' || agreementStatus === 'pending') &&
              !canSubmit
            "
          >
            <span class="mr-6 ml-6">Share with Employee</span>
          </Button>

          <Button
            :disabled="disableBtn"
            options
            :option-list="['Approve', 'Return to Employee']"
            :class="
              disableBtn ? `btn-disabled` : `bg-dynamicBackBtn text-white`
            "
            @click="checkSelected($event, 'all')"
            v-else-if="agreementStatus === 'submitted'"
          >
            <span class="mr-6 ml-6">Approve</span>
          </Button>

          <Button
            background-color="none"
            style="color: #321c3b"
            @click="$router.back()"
          >
            Cancel
          </Button>
        </div>
      </div>
    </ValidationObserver>

    <RightSideBar
      v-if="commentModal"
      @submit="sendComment"
      @close="(commentModal = false), (senderkpaId = ''), (comment = '')"
      submit="Save"
      button-class="bg-dynamicBackBtn text-white"
      :disabled-button="isSavingComment"
    >
      <template v-slot:title>
        <span class="text-darkPurple font-bold text-lg leading-5 p-0"
          >Comments</span
        >
      </template>
      <div class="w-full flex mb-7">
        <div class="flex flex-col w-full" style="min-height: 70vh">
          <CommentsBox
            :comments="allComments"
            :senderId="senderId"
            @markRead="markAsRead($event)"
            @deleteComment="deleteComments($event)"
            :loading="loadComments"
          />
          <div class="-mb-12" style="margin-top: auto">
            <CTextarea
              placeholder="--Message--"
              variant="primary"
              class="mt-2 rounded-lg"
              value=""
              :row="8"
              :height="90"
              :col="30"
              :input="null"
              v-model="comment"
            />
          </div>
        </div>
      </div>
    </RightSideBar>

    <RightSideBar
      v-if="statusModal"
      @close="statusModal = false"
      v-bind="
        senderId !== $route.params.id
          ? { closeButton: true }
          : { submit: 'Save' }
      "
      @submit="saveReport"
      button-class="bg-dynamicBackBtn text-white"
      :disabled-button="isSavingReport"
    >
      <template v-slot:title>
        <div class="w-full flex justify-between items-center">
          <span class="text-darkPurple font-bold text-lg leading-5 p-0"
            >Report New</span
          >
          <span
            v-if="senderId === $route.params.id"
            class="text-blueCrayola font-bold text-sm uppercase cursor-pointer flex"
            @click="
              $router.push({
                name: 'EssEmployeeStatusReports',
                params: { id: $route.params.id, year: $route.params.year },
              })
            "
          >
            <Icon
              icon-name="history-icon"
              class="text-blueCrayola"
              size="xms"
            />
            History
          </span>
        </div>
      </template>
      <StatusReport
        :kpasItem="kpasItem"
        :kpaTypes="kpaTypes"
        :edit="true"
        :isView="senderId !== $route.params.id"
        @update-files="updateFiles"
        @update-comment="updateComment"
        @update-support="updateSupport"
        @update-status="updateStatus"
        @kpis-criteriaValues="kpisCriteriaValues"
      />
    </RightSideBar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Datepicker from "vuejs-datepicker";
import Badge from "@scelloo/cloudenly-ui/src/components/badge";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import Table from "@scelloo/cloudenly-ui/src/components/table";
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Card from "@/components/Card";
import Button from "@/components/Button";
import ScrollContainer from "@/components/ScrollContainer";
import StatusReport from "@/components/StatusReport";
import Accordion from "@/components/Accordion";
import RightSideBar from "@/components/RightSideBar";
import CommentsBox from "@/components/commentsBox";
import timeFormatMixin from "@/utilities/timeFormatMixin";

export default {
  name: "EmployeeAgreement",
  mixins: [timeFormatMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    BackButton,
    Card,
    Datepicker,
    StatusReport,
    CommentsBox,
    CTextarea,
    Badge,
    RightSideBar,
    CText,
    Breadcrumb,
    Table,
    Button,
    ScrollContainer,
    Accordion,
  },
  data() {
    return {
      loadingData: true,
      commentModal: false,
      statusModal: false,
      disableBtn: false,
      allocatedWeight: 0,
      agreementId: "",
      approvedSubmit: false,
      comment: "",
      canSubmit: false,
      managerLock: false,
      editingLock: false,
      approvedAdmin: false,
      agreementStatus: "",
      approved: false,
      employeeSubmitted: "",
      loadComments: true,
      allComments: [],
      unreadComments: "",
      employee: [],
      Ratings: [],
      templateKPATypes: [],
      kpaName: [],
      breadcrumbs: [
        {
          disabled: false,
          text: "Performance",
          href: "Performance",
          id: "Performance",
        },
        {
          disabled: false,
          text: "Performance Agreement",
          href: "Performance Agreement",
          id: "PerformanceAgreement",
        },
        {
          disabled: false,
          text: "Employee Agreement",
          href: "employee-agreement",
          id: "EmployeeAgreement",
        },
      ],
      kpaItems: [],
      weightRules: [
        "required",
        {
          name: "weight",
          rule: (w) => this.validateWeight(w),
        },
      ],
      KPAHeader: [
        { title: "•", value: "state" },
        { title: "KPI", value: "name" },
        { title: "Objective", value: "objective" },
        { title: "Criteria", value: "kpis" },
        { title: "Target Date", value: "target_date" },
        { title: "Weight (%)", value: "weight" },
      ],
      payload: {
        orgId: "",
        templateId: "",
        title: "",
        goalId: "",
        status: "draft",
        created_by: "",
        created_for: [],
        share_for_review: false,
        kpa_types: [],
      },
      senderkpaId: "",
      senderId: "",
      kpasItem: {},
      kpaTypes: {},
      files: [],
      reportComment: "",
      requireSupport: "",
      reportStatus: "",
      currentAchievement: "",
      criteriaValues: "",
      isSavingComment: false,
      isSavingReport: false,
    };
  },

  created() {
    this.senderId = this.$AuthUser.id;
    this.$_viewEmployeeAgreements(this.$route.params.id).then((result) => {
      this.employee = result.data.user;
      const agreementKPA = result.data.EmployeePerformanceAgreements;

      agreementKPA.forEach((agreement) => {
        this.agreementId = agreement.id;
        this.approvedAdmin = agreement.approvedByAdmin;
        this.approved = agreement.approveAndSubmit;
        this.agreementStatus = agreement.agreement_status;
        this.canSubmit = agreement.canSubmit;
        this.payload.templateId = agreement.templateId;
        this.payload.agreementId = agreement.id;
        this.payload.title = agreement.description;
        this.payload.description = agreement.description;
        this.employeeSubmitted = agreement.agreement_status;
        this.payload.year = agreement.year;
        this.payload.status = agreement.status;
        this.approvedSubmit = agreement.approveAndSubmit;
        this.payload.created_for = agreement.created_for;
        this.payload.created_by = agreement.created_for;

        const performanceKPA = agreement.employee_agreement_kpa;

        let totalWeight = 0;
        performanceKPA.forEach((kpaType) => {
          totalWeight += Number(kpaType.weight) || 0;

          if (this.kpaName.includes(kpaType.kpaTypeId)) {
            this.templateKPATypes.filter((detail) => {
              if (detail.id === kpaType.kpaTypeId) {
                detail.kpas.push({
                  state: true,
                  isEdit: false,
                  isLocked: kpaType.isLocked,
                  name: kpaType.name,
                  objective: kpaType.objective,
                  kpaId: kpaType.id,
                  kpis: this.getCriteriaData(kpaType.kpis_employee),
                  target_date: this.formatEmployeeDate(kpaType.targetDate),
                  weight: kpaType.weight,
                  report: kpaType.report,
                });
              }
              return {};
            });
          } else {
            this.templateKPATypes.push({
              name: kpaType.kpa_type.name,
              description: kpaType.kpa_type.description,
              id: kpaType.kpaTypeId,
              kpas: [
                {
                  state: true,
                  isEdit: false,
                  isLocked: kpaType.isLocked,
                  name: kpaType.name,
                  objective: kpaType.objective,
                  kpaId: kpaType.id,
                  kpis: this.getCriteriaData(kpaType.kpis_employee),
                  target_date: this.formatEmployeeDate(kpaType.targetDate),
                  weight: kpaType.weight,
                  report: kpaType.report,
                },
              ],
            });
            this.kpaName.push(kpaType.kpaTypeId);
          }
          return {};
        });
        this.allocatedWeight = totalWeight;
      });
    });

    this.$_getPerfomanceRating().then((response) => {
      this.Ratings = response.data.PerformanceRatings;

      const sortedRatings = this.Ratings.sort((a, b) => a.rating - b.rating);

      this.Ratings = sortedRatings;
    });
  },
  methods: {
    async viewStatusReports(id, year) {
      this.$router.push({
        name: "EssEmployeeStatusReports",
        params: { id, year },
      });
    },

    isKpaEditable(kpaTypes) {
      return kpaTypes.kpas.some((kpa) => kpa.isEdit);
    },

    toggleEditMode(kpaTypes) {
      if (!this.isKpaEditable(kpaTypes)) {
        kpaTypes.kpas.forEach((kpa) => {
          this.$set(kpa, "isEdit", true);
        });
      } else {
        kpaTypes.kpas.forEach((kpa) => {
          this.$set(kpa, "isEdit", false);
        });
        this.submit("active");
      }
    },

    setAllKpasEditable(kpaTypes) {
      kpaTypes.kpas.forEach((kpa) => {
        this.$set(kpa, "isEdit", true);
      });
    },

    lockKPA(isLocked, data) {
      if (this.managerLock) {
        data.isLocked = !isLocked;
      } else {
        this.$toasted.error(
          "You do not have the privilege to lock or unlock an agreement",
          { duration: 5000 }
        );
      }
    },

    deleteKPA(index, kpaTypes) {
      if (kpaTypes.kpas.length > 1) {
        if (!this.approvedSubmit) {
          kpaTypes.kpas.splice(index, 1);

          this.$_deleteAgreementKPI(kpaTypes.id).then((response) => {
            let totalWeight = 0;
            this.templateKPATypes.forEach((kpaType) => {
              kpaType.kpas.forEach((kpi) => {
                totalWeight += Number(kpi.weight) || 0;
              });
            });
            this.allocatedWeight = totalWeight;
            this.$toasted.success(response.data.message, { duration: 5000 });
          });
        }
      } else {
        this.$toasted.error(
          "Cannot delete ALL KPIs of a KPA Type, you can edit KPI values instead",
          { duration: 5000 }
        );
      }
    },

    validateWeight(weight) {
      let sum = 0;
      if (weight < 0) {
        return "Value must be greater than or equal to 0";
      }

      this.templateKPATypes.forEach((kpaType) => {
        kpaType.kpas.forEach((kpi) => {
          sum += Number(kpi.weight);
        });
      });

      // if (sum > 100) {
      //   return "Total allocated weight for all KPA's cannot be greater than 100%";
      // }

      this.allocatedWeight = sum;

      if (sum < 100) {
        this.disableBtn = true;
      } else {
        this.disableBtn = false;
      }

      return true;
    },

    // handleCommentModal() {
    //   this.commentModal = !this.commentModal;
    // },

    getKPAWeight(kpaTypes, kpaTypesId) {
      let sum = 0;

      if (kpaTypes.id === kpaTypesId) {
        kpaTypes.kpas.forEach((kpi) => {
          sum += Number(kpi.weight);
        });
      }

      return sum;
    },

    createNewKPA(kpa) {
      const currentIndex = kpa.kpas.length - 1;
      const arrayObj = kpa.kpas[currentIndex];
      let isEmpty = true;

      if (
        arrayObj.name !== "" &&
        arrayObj.objective !== "" &&
        arrayObj.weight !== ""
      ) {
        // iterate over criteria array
        arrayObj.kpis.forEach((v) => {
          if (Object.keys(v).every((k) => v[k] !== "")) {
            isEmpty = false;
          }
        });
      }

      this.addKPATable(kpa.id, isEmpty);
    },

    addKPATable(kpaId, valid) {
      if (!valid) {
        if (this.allocatedWeight < 100) {
          this.templateKPATypes.forEach((kpaType) => {
            if (kpaType.id === kpaId) {
              kpaType.kpas.push({
                state: true,
                isEdit: true,
                isLocked: false,
                name: null,
                objective: null,
                kpis: this.getCriteriaData(),
                target_date: "",
                weight: "",
              });
            }
            return {};
          });
        } else {
          this.$toasted.error(
            "Total allocated weight for all KPA's cannot be greater than 100%",
            { duration: 5000 }
          );
        }
      }
    },

    approveAgreement() {
      const agreementArr = [];

      agreementArr.push(this.agreementId);

      const payload = {
        agreementIds: agreementArr,
        approvedByAdmin: true,
        approveAndSubmit: true,
      };

      this.disableBtn = true;

      this.$_adminApproveAgreement(payload)
        .then((response) => {
          this.$toasted.success(`${response.data.message}`, { duration: 5000 });
          this.$router.back();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    submit(status, mode) {
      this.kpaData = [];
      let totalWeight = 0;
      let allValid = true;

      this.templateKPATypes.forEach((kpaType) => {
        const newKPAs = kpaType.kpas.map(
          ({ state, isEdit, target_date, ...rest }) => ({
            ...rest,
          })
        );

        const validKPAs = newKPAs.every((kpa) => {
          const hasCoreFields =
            kpa.name?.trim() &&
            kpa.objective?.trim() &&
            kpa.weight !== null &&
            kpa.weight !== "" &&
            !isNaN(Number(kpa.weight));

          const hasValidKPIs =
            Array.isArray(kpa.kpis) &&
            kpa.kpis.length > 0 &&
            kpa.kpis.every(
              (kpi) => kpi.criteria?.trim() && kpi.performanceRatingId?.trim()
            );

          return hasCoreFields && hasValidKPIs;
        });

        if (!validKPAs) {
          allValid = false;
        }

        newKPAs.forEach((kpa) => {
          totalWeight += Number(kpa.weight) || 0;
        });

        this.kpaData.push({
          kpa_types_id: kpaType.id || kpaType.kpa_types_id,
          description: kpaType.description,
          kpas: newKPAs,
        });
      });

      if (!allValid) {
        this.$toasted.error(
          "Please fill all KPA fields and KPI fields before submitting.",
          {
            duration: 5000,
          }
        );
        return;
      }

      if (totalWeight !== 100) {
        this.$toasted.error(
          `Total KPA weight must equal 100%. Current: ${totalWeight}%`,
          {
            duration: 5000,
          }
        );
        return;
      }

      this.payload.kpa_types = this.kpaData;
      this.payload.orgId = this.$orgId;
      this.payload.status = status;
      this.payload.share_for_review = true;
      this.payload.agreement_status = "shared";
      this.payload.senderId = this.$AuthUser.id;

      this.$_createEmployeeAgreement(this.payload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          if (mode === "all") {
            this.$router.back();
          }
        })
        .catch((error) => {
          this.$toasted.error(error.response.data.message, { duration: 5000 });
        });
    },

    formatCommentTime(date) {
      const newDate = new Date(date);
      const hours = newDate.getUTCHours();
      const minutes = newDate.getMinutes();

      return `${1 + ((hours - 1) % 12)}:${minutes
        .toString()
        .padStart(2, "0")} ${hours > 11 ? "PM" : "AM"}`;
    },

    formatCommentDate(date) {
      const newDate = new Date(date);
      return newDate.toDateString();
    },

    async sendComment() {
      this.isSavingComment = true;
      const payload = {
        performanceAgreementId: this.agreementId,
        senderId: this.senderId, // userId of sender
        receiverId: this.$route.params.id, // userId of receiver
        comment: this.comment,
        sendTo: "admin",
        ...(this.senderkpaId && { kpiId: this.senderkpaId }),
      };

      try {
        await this.$_sendAgreementComments(payload);
        this.allComments = [];
        this.comment = "";
        this.commentModal = false;
        this.isSavingComment = false;
        this.$toasted.success("Comment successfully sent", { duration: 5000 });
      } catch (error) {
        this.isSavingComment = false;
        this.$toasted.error("Error sending comment", { duration: 5000 });
      }
    },

    deleteComments(id) {
      this.$_deleteAgreementComments(id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          // this.getComments(this.agreementId);
        })
        .then((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    markAsRead(commentId) {
      const payload = {
        commentId,
        readStatus: true,
      };

      this.$_markAsRead(payload)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 5000 });
          // this.getComments(this.agreementId);
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    getCriteriaData(kpis) {
      const criteriaData = [];
      const sortedRatings = this.Ratings.sort((a, b) => a.rating - b.rating);
      const sortedKPIS = kpis
        ? kpis.sort((a, b) => a.score.rating - b.score.rating)
        : null;

      this.Ratings = sortedRatings;

      if (kpis) {
        this.Ratings.forEach((rating, index) => {
          criteriaData.push({
            id: sortedKPIS[index].id,
            performanceRatingId: rating.id,
            criteria: sortedKPIS[index].criteria,
          });
        });
      } else {
        this.Ratings.map((rating) => {
          criteriaData.push({
            performanceRatingId: rating.id,
            criteria: "",
          });
          return {};
        });
      }

      this.loadingData = false;
      return criteriaData;
    },

    formatEmployeeDate(value) {
      let initialDate = "";

      if (value) {
        initialDate = new Date(value).toISOString().replace(/.\d+Z$/g, "");

        return this.$DATEFORMAT(new Date(initialDate), "MMMM dd, yyyy");
      }

      return "";
    },

    checkSelected(value, mode) {
      if (value === "Approve") {
        this.approveAgreement();
      } else if (value === "Share with Employee") {
        this.submit("active", mode);
      } else {
        this.returnAgreement();
      }
    },

    returnAgreement() {
      const agreementIdsArr = [];
      agreementIdsArr.push(this.agreementId);

      const payload = {
        agreementIds: agreementIdsArr,
        approvedByAdmin: false,
        approveAndSubmit: false,
      };

      this.$_returnEmployeeAgreement(payload)
        .then((response) => {
          this.$toasted.success(
            `${response.data.message} returned successfully`,
            { duration: 5000 }
          );
          this.$router.back();
        })
        .catch((err) => {
          this.$toasted.error(err.response.data.message, { duration: 5000 });
        });
    },

    getSettings() {
      this.$_getPerformanceGeneralSetting().then((response) => {
        const settings = response.data.PerformanceSettings;

        this.managerLock = settings[0].managerCanLockAgreement;

        if (this.approvedSubmit) {
          this.editingLock = settings[0].lockSubmittedAgreement;
        }
      });
    },

    getGoals() {
      this.$_getOrganizationGoal("?filter=agreement").then((response) => {
        const goalData = response.data.goals;
        goalData.forEach((goals) => {
          if (goals.goal.year === Number(this.$route.params.year)) {
            this.payload.goalId = goals.goal.id;
          }
          return {};
        });
      });
    },
    async getEachAgreementComments(kpiId = "") {
      this.senderkpaId = kpiId;
      const kpi = kpiId ? `?kpiId=${kpiId}` : "";
      this.loadComments = true;
      try {
        const { data } = await this.$_getEachAgreementComments(
          this.agreementId,
          kpi
        );
        this.allComments = data.comments;
        this.commentModal = true;
        this.loadComments = false;
      } catch (error) {
        this.loadComments = false;
        this.senderkpaId = "";
        this.$toasted.error("Error getting comments", { duration: 5000 });
      }
    },
    updateFiles(files) {
      this.files = files;
    },
    updateSupport(support) {
      this.requireSupport = support;
    },
    updateComment(comment) {
      this.reportComment = comment;
    },
    updateStatus(status) {
      this.reportStatus = status;
    },
    kpisCriteriaValues(criteriaValues) {
      this.criteriaValues = criteriaValues;
    },
    async saveReport() {
      this.isSavingReport = true;
      const payload = {
        orgId: this.$orgId,
        employeeId: this.senderId,
        employeeAgreementId: this.agreementId,
        kpaTypeId: this.kpaTypes.id,
        kpiId: this.kpasItem.kpaId,
        status: this.reportStatus,
        criteria: this.criteriaValues,
        currentAchievement: "",
        requireSupport: this.requireSupport === "yes" ? true : false,
        comment: this.reportComment,
        files: this.files,
        ...(this.kpasItem.report &&
          this.kpasItem.report !== "null" && {
            reportId: this.kpasItem.report.id,
          }),
      };
      try {
        await this.$_saveReport(payload);
        this.statusModal = false;
        this.isSavingReport = false;
        this.$toasted.success("Report done successfully", { duration: 5000 });
      } catch (error) {
        this.isSavingReport = false;
        this.$toasted.error(error.response.data.message, { duration: 5000 });
      }
    },
    async viewReport(kpasItem, kpaTypes) {
      this.statusModal = true;
      this.kpasItem = kpasItem;
      this.kpaTypes = kpaTypes;
    },
  },

  mounted() {
    this.getGoals();
    this.getSettings();
  },
};
</script>

<style>
.cloudenly-input {
  padding-right: 8px !important;
  padding-left: 8px !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  height: 38px !important;
}
</style>

<style>
.bg-disabled {
  background-color: #eeeeee;
  border-color: transparent;
}

.label label {
  display: block;
  font-size: 10px;
}
</style>
